import is from '@sindresorhus/is';
import type { ReactNode } from 'react';

import {
    createUrlTileCodec,
    createUrlTileDefinition,
    type UrlTileCodec,
    type UrlTileDefinition,
    type UrlTilePayload,
    type UrlTileRenderContext,
    type UrlTileSegmentValueCodec,
    urlTileIntegerCodec,
    urlTileKeywordCodec,
    urlTileStringCodec,
    urlTileVersionNumberCodec,
    urlTileVersionStringCodec,
} from '../../url-tiles';
import { ArchivedItemsPanel } from '../archive-sidepanel/ArchivedItemsPanel';
import { ChangelogDetails } from '../changelog-sidepanel/Details';
import { CompareVersion } from '../compare/CompareVersion';
import { JobArchitecturePermissionsContextProvider } from '../context/JobArchitecturePermissionsContext';
import { JobClusterDetails } from '../job-cluster-sidepanel/Details';
import { JobFamilyDetails } from '../job-family-sidepanel/Details';
import {
    AUTOMATION_ASSESSMENT_TILE_SLOT_KEY,
    AUTOMATION_ASSESSMENT_TILE_WIDTH,
    AUTOMATION_RECOMMENDATION_TILE_SLOT_KEY,
    AUTOMATION_RECOMMENDATION_TILE_WIDTH,
    AUTOMATION_STEP_TILE_SLOT_KEY,
    AUTOMATION_STEP_TILE_WIDTH,
    AutomationAssessmentTile,
    type AutomationAssessmentTilePayload,
    AutomationRecommendationTile,
    type AutomationRecommendationTilePayload,
    AutomationStepTile,
    type AutomationStepTilePayload,
    automationAssessmentTileSchema,
    automationRecommendationTileSchema,
    automationStepTileSchema,
    BUSINESS_IMPACT_TILE_WIDTH,
    BusinessImpactTile,
    type BusinessImpactTilePayload,
    businessImpactTileSchema,
    JSA_AUTOMATION_ASSESSMENT_TILE_TYPE,
    JSA_AUTOMATION_RECOMMENDATION_TILE_TYPE,
    JSA_AUTOMATION_STEP_TILE_TYPE,
    JSA_BUSINESS_IMPACT_TILE_TYPE,
} from '../profile-tasks/automation-assessment';
import { ProfileTaskTile } from '../profile-tasks/tile/profile-task-tile/ProfileTaskTile';
import { JSA_PROFILE_TASK_DETAIL_TILE_TYPE } from '../profile-tasks/tile/profile-task-tile-types';
import {
    PROFILE_TASK_TILE_SLOT_KEY,
    PROFILE_TASK_TILE_WIDTH,
    type ProfileTaskTilePayload,
    parseProfileTaskTileTokens,
    profileTaskTileSchema,
    serializeProfileTaskTilePayload,
} from '../profile-tasks/tile/task-tile-url';
import { JobArchitectureContentLanguageProvider } from '../providers/JobArchitectureLanguageProvider';
import { RoleComparison } from '../role-comparison/RoleComparison';
import { RoleLevelComparison } from '../role-level-comparison/RoleLevelComparison';
import { JobArchitectureScopeProvider } from '../url-navigation/job-architecture-scope';
import {
    JSA_ARCHIVE_TILE_MAX_WIDTH,
    JSA_ARCHIVE_TILE_SLOT_KEY,
    JSA_ARCHIVE_TILE_TYPE,
    JSA_CHANGELOG_TILE_SLOT_KEY,
    JSA_CHANGELOG_TILE_TYPE,
    JSA_CHANGELOG_TILE_WIDTH,
    JSA_CLUSTER_DETAIL_TILE_TYPE,
    JSA_COMPARE_TILE_SLOT_KEY,
    JSA_COMPARE_TILE_TYPE,
    JSA_FAMILY_DETAIL_TILE_TYPE,
    JSA_LEVEL_COMPARISON_TILE_SLOT_KEY,
    JSA_LEVEL_COMPARISON_TILE_TYPE,
    JSA_LEVEL_COMPARISON_TILE_WIDTH,
    JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    JSA_PROFILE_DETAIL_TILE_TYPE,
    JSA_ROLE_COMPARISON_TILE_SLOT_KEY,
    JSA_ROLE_COMPARISON_TILE_TYPE,
    JSA_ROLE_COMPARISON_TILE_WIDTH,
    type JsaArchiveTilePayload,
    type JsaChangelogTilePayload,
    type JsaClusterDetailTilePayload,
    type JsaCompareTilePayload,
    type JsaFamilyDetailTilePayload,
    type JsaLevelComparisonTilePayload,
    type JsaProfileDetailTilePayload,
    type JsaRoleComparisonTilePayload,
    jsaArchiveTileSchema,
    jsaChangelogTileSchema,
    jsaClusterDetailTileSchema,
    jsaCompareTileSchema,
    jsaFamilyDetailTileSchema,
    jsaLevelComparisonTileSchema,
    jsaProfileDetailTileSchema,
    jsaRoleComparisonTileSchema,
} from '../url-tile-payloads';
import { WorkTaskTile } from '../work-task-tile/WorkTaskTile';
import {
    createWorkTaskTilePayload,
    JSA_WORK_TASK_TILE_TYPE,
    WORK_TASK_TILE_SLOT_KEY,
    WORK_TASK_TILE_WIDTH,
    type WorkTaskTilePayload,
    workTaskTileSchema,
} from '../work-task-tile/work-task-tile-url';
import { JobProfileTile } from './JobProfileTile';

const preferredLanguageOption = {
    codec: urlTileStringCodec,
    field: 'preferredLanguage',
    key: 'lang',
} as const;

// Item ids travel as one comma-joined segment so the tile type keeps a fixed segment count.
const urlTileIdListCodec: UrlTileSegmentValueCodec<readonly string[]> = {
    decode: (token) => {
        const ids = token.split(',').filter(is.nonEmptyString);

        return ids.length > 0 ? ids : undefined;
    },
    encode: (ids) => ids.join(','),
};

const notificationRowOption = {
    codec: urlTileStringCodec,
    field: 'notificationRow',
    key: 'notificationRow',
} as const;

const JSA_PROFILE_TILE_WIDTH = '50%';

const PROFILE_TILE_TAB_KEYWORDS = { overview: 'overview', positions: 'positions', tasks: 'tasks' } as const;

type JsaProfileTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaProfileDetailTilePayload;
};

type JsaFamilyTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaFamilyDetailTilePayload;
};

type JsaClusterTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaClusterDetailTilePayload;
};

type JsaCompareTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaCompareTilePayload;
};

type JsaChangelogTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaChangelogTilePayload;
};

type JsaArchiveTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaArchiveTilePayload;
};

type JsaLevelComparisonTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaLevelComparisonTilePayload;
};

type JsaRoleComparisonTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: JsaRoleComparisonTilePayload;
};

type ProfileTaskTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: ProfileTaskTilePayload;
};

type WorkTaskTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: WorkTaskTilePayload;
};

type AutomationAssessmentTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: AutomationAssessmentTilePayload;
};

type BusinessImpactTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: BusinessImpactTilePayload;
};

type AutomationRecommendationTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: AutomationRecommendationTilePayload;
};

type AutomationStepTileDefinitionProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: AutomationStepTilePayload;
};

export const jsaProfileDetailUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<JsaProfileDetailTilePayload>({
        options: [
            { codec: urlTileKeywordCodec({ true: true }), field: 'isArchived', key: 'archived' },
            { codec: urlTileKeywordCodec({ closed: false, open: true }), field: 'isCommentsOpen', key: 'comments' },
            preferredLanguageOption,
            notificationRowOption,
            { codec: urlTileKeywordCodec(PROFILE_TILE_TAB_KEYWORDS), field: 'tab', key: 'tab' },
            { codec: urlTileStringCodec, field: 'positionArchitectureId', key: 'positions-of' },
        ],
        segments: [
            'architectureId',
            'itemType',
            'profileId',
            'mode',
            { codec: urlTileVersionNumberCodec, field: 'version', optional: true },
        ],
        type: JSA_PROFILE_DETAIL_TILE_TYPE,
    }),
    commentsGroup: {
        groupKey: (payload) => `${payload.architectureId}.${payload.profileId}`,
        isCommentsOpen: (payload) => payload.isCommentsOpen === true,
        role: 'parent',
    },
    maxWidth: JSA_PROFILE_TILE_WIDTH,
    minWidth: JSA_PROFILE_TILE_WIDTH,
    render: ({ context, payload }: JsaProfileTileProps) => (
        <JobProfileTile
            close={() => context.closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)}
            host={context.host}
            payload={payload}
        />
    ),
    schema: jsaProfileDetailTileSchema,
    slotKey: () => JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${payload.architectureId}.${payload.profileId}`,
    toProps: (payload, context) => ({ context, payload }),
});

export const jsaFamilyDetailUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<JsaFamilyDetailTilePayload>({
        options: [preferredLanguageOption, notificationRowOption],
        segments: ['architectureId', 'familyId', 'mode'],
        type: JSA_FAMILY_DETAIL_TILE_TYPE,
    }),
    maxWidth: '50%',
    minWidth: '50%',
    render: ({ context, payload }: JsaFamilyTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <JobArchitectureContentLanguageProvider initialContentLanguage={payload.preferredLanguage}>
                <JobFamilyDetails
                    architectureId={payload.architectureId}
                    familyId={payload.familyId}
                    mode={payload.mode}
                    onActiveLanguageChange={(preferredLanguage) =>
                        context.updateTile({ ...payload, preferredLanguage })
                    }
                    onClose={() => context.closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)}
                />
            </JobArchitectureContentLanguageProvider>
        </JobArchitectureScopeProvider>
    ),
    schema: jsaFamilyDetailTileSchema,
    slotKey: () => JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${payload.architectureId}.${payload.familyId}`,
    toProps: (payload, context) => ({ context, payload }),
});

export const jsaClusterDetailUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<JsaClusterDetailTilePayload>({
        options: [preferredLanguageOption, notificationRowOption],
        segments: ['architectureId', 'familyId', 'clusterId', 'mode'],
        type: JSA_CLUSTER_DETAIL_TILE_TYPE,
    }),
    maxWidth: '50%',
    minWidth: '50%',
    render: ({ context, payload }: JsaClusterTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <JobArchitectureContentLanguageProvider initialContentLanguage={payload.preferredLanguage}>
                <JobClusterDetails
                    architectureId={payload.architectureId}
                    clusterId={payload.clusterId}
                    familyId={payload.familyId}
                    mode={payload.mode}
                    onActiveLanguageChange={(preferredLanguage) =>
                        context.updateTile({ ...payload, preferredLanguage })
                    }
                    onClose={() => context.closeTile(JSA_PRIMARY_DETAIL_TILE_SLOT_KEY)}
                />
            </JobArchitectureContentLanguageProvider>
        </JobArchitectureScopeProvider>
    ),
    schema: jsaClusterDetailTileSchema,
    slotKey: () => JSA_PRIMARY_DETAIL_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_PRIMARY_DETAIL_TILE_SLOT_KEY}.${payload.architectureId}.${payload.clusterId}`,
    toProps: (payload, context) => ({ context, payload }),
});

export const jsaCompareUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<JsaCompareTilePayload>({
        options: [{ codec: urlTileStringCodec, field: 'proposalBlockId', key: 'proposal' }, preferredLanguageOption],
        segments: [
            'architectureId',
            'itemType',
            'profileId',
            { codec: urlTileVersionStringCodec, field: 'versionA' },
            { codec: urlTileVersionStringCodec, field: 'versionB' },
        ],
        type: JSA_COMPARE_TILE_TYPE,
    }),
    maxWidth: '50%',
    minWidth: '50%',
    render: ({ context, payload }: JsaCompareTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <CompareVersion
                architectureId={payload.architectureId}
                itemId={payload.profileId}
                itemType={payload.itemType}
                preferredLanguage={payload.preferredLanguage}
                proposalBlockId={payload.proposalBlockId}
                versionA={payload.versionA}
                versionB={payload.versionB}
                onClose={() => context.closeTile(JSA_COMPARE_TILE_SLOT_KEY)}
            />
        </JobArchitectureScopeProvider>
    ),
    schema: jsaCompareTileSchema,
    slotKey: () => JSA_COMPARE_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_COMPARE_TILE_SLOT_KEY}.${payload.architectureId}.${payload.profileId}`,
    toProps: (payload, context) => ({ context, payload }),
});

export const jsaChangelogUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<JsaChangelogTilePayload>({
        options: [preferredLanguageOption],
        segments: ['architectureId'],
        type: JSA_CHANGELOG_TILE_TYPE,
    }),
    maxWidth: JSA_CHANGELOG_TILE_WIDTH,
    minWidth: JSA_CHANGELOG_TILE_WIDTH,
    render: ({ context, payload }: JsaChangelogTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <JobArchitectureContentLanguageProvider initialContentLanguage={payload.preferredLanguage}>
                <ChangelogDetails onClose={() => context.closeTile(JSA_CHANGELOG_TILE_SLOT_KEY)} />
            </JobArchitectureContentLanguageProvider>
        </JobArchitectureScopeProvider>
    ),
    schema: jsaChangelogTileSchema,
    slotKey: () => JSA_CHANGELOG_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_CHANGELOG_TILE_SLOT_KEY}.${payload.architectureId}`,
    toProps: (payload, context) => ({ context, payload }),
});

export const jsaArchiveUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<JsaArchiveTilePayload>({
        segments: ['architectureId'],
        type: JSA_ARCHIVE_TILE_TYPE,
    }),
    maxWidth: JSA_ARCHIVE_TILE_MAX_WIDTH,
    minWidth: JSA_ARCHIVE_TILE_MAX_WIDTH,
    render: ({ context, payload }: JsaArchiveTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <JobArchitecturePermissionsContextProvider>
                <ArchivedItemsPanel onClose={() => context.closeTile(JSA_ARCHIVE_TILE_SLOT_KEY)} />
            </JobArchitecturePermissionsContextProvider>
        </JobArchitectureScopeProvider>
    ),
    schema: jsaArchiveTileSchema,
    slotKey: () => JSA_ARCHIVE_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_ARCHIVE_TILE_SLOT_KEY}.${payload.architectureId}`,
    toProps: (payload, context) => ({ context, payload }),
});

export const jsaLevelComparisonUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<JsaLevelComparisonTilePayload>({
        options: [
            { codec: urlTileKeywordCodec({ hidden: false }), field: 'showProficiency', key: 'proficiency' },
            preferredLanguageOption,
        ],
        segments: ['architectureId', 'roleId'],
        type: JSA_LEVEL_COMPARISON_TILE_TYPE,
    }),
    maxWidth: JSA_LEVEL_COMPARISON_TILE_WIDTH,
    minWidth: JSA_LEVEL_COMPARISON_TILE_WIDTH,
    render: ({ context, payload }: JsaLevelComparisonTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <RoleLevelComparison
                architectureId={payload.architectureId}
                openProfilesOnStandalonePage={
                    context.location.pathname === '/job-profile' ||
                    context.location.pathname.startsWith('/job-profile/')
                }
                preferredLanguage={payload.preferredLanguage}
                roleId={payload.roleId}
                showProficiency={payload.showProficiency}
                onClose={() => context.closeTile(JSA_LEVEL_COMPARISON_TILE_SLOT_KEY)}
            />
        </JobArchitectureScopeProvider>
    ),
    schema: jsaLevelComparisonTileSchema,
    slotKey: () => JSA_LEVEL_COMPARISON_TILE_SLOT_KEY,
    tileKey: (payload) => `${JSA_LEVEL_COMPARISON_TILE_SLOT_KEY}.${payload.architectureId}.${payload.roleId}`,
    toProps: (payload, context) => ({ context, payload }),
});

export const jsaRoleComparisonUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<JsaRoleComparisonTilePayload>({
        options: [
            { codec: urlTileKeywordCodec({ hidden: false }), field: 'showProficiency', key: 'proficiency' },
            preferredLanguageOption,
        ],
        segments: ['architectureId', 'itemType', { codec: urlTileIdListCodec, field: 'itemIds' }],
        type: JSA_ROLE_COMPARISON_TILE_TYPE,
    }),
    maxWidth: JSA_ROLE_COMPARISON_TILE_WIDTH,
    minWidth: JSA_ROLE_COMPARISON_TILE_WIDTH,
    render: ({ context, payload }: JsaRoleComparisonTileProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <RoleComparison
                architectureId={payload.architectureId}
                openProfilesOnStandalonePage={
                    context.location.pathname === '/job-profile' ||
                    context.location.pathname.startsWith('/job-profile/')
                }
                itemIds={payload.itemIds}
                itemType={payload.itemType}
                preferredLanguage={payload.preferredLanguage}
                showProficiency={payload.showProficiency}
                onClose={() => context.closeTile(JSA_ROLE_COMPARISON_TILE_SLOT_KEY)}
            />
        </JobArchitectureScopeProvider>
    ),
    schema: jsaRoleComparisonTileSchema,
    slotKey: () => JSA_ROLE_COMPARISON_TILE_SLOT_KEY,
    tileKey: (payload) =>
        `${JSA_ROLE_COMPARISON_TILE_SLOT_KEY}.${payload.architectureId}.${payload.itemType}.${payload.itemIds.join(',')}`,
    toProps: (payload, context) => ({ context, payload }),
});

export const profileTaskUrlTileDefinition = createUrlTileDefinition({
    // The trailing version token doubles as the draft marker, so this tile keeps a
    // hand-written codec instead of a declarative segment list.
    codec: {
        parseTokens: parseProfileTaskTileTokens,
        serialize: serializeProfileTaskTilePayload,
        type: JSA_PROFILE_TASK_DETAIL_TILE_TYPE,
    },
    commentsGroup: {
        groupKey: (payload) => `${payload.architectureId}.${payload.parentId}`,
        isCommentsOpen: (payload) => payload.isCommentsOpen === true,
        parentType: JSA_PROFILE_DETAIL_TILE_TYPE,
        role: 'child',
    },
    maxWidth: PROFILE_TASK_TILE_WIDTH,
    minWidth: PROFILE_TASK_TILE_WIDTH,
    render: ({ context, payload }: ProfileTaskTileDefinitionProps) => (
        <JobArchitectureScopeProvider architectureId={payload.architectureId}>
            <JobArchitecturePermissionsContextProvider>
                <ProfileTaskTile
                    payload={payload}
                    onClose={() => context.closeTile(PROFILE_TASK_TILE_SLOT_KEY)}
                    onRestorePayload={(previousPayload) => context.updateTile(previousPayload)}
                />
            </JobArchitecturePermissionsContextProvider>
        </JobArchitectureScopeProvider>
    ),
    schema: profileTaskTileSchema,
    slotKey: () => PROFILE_TASK_TILE_SLOT_KEY,
    tileKey: ({ taskId }) => `${PROFILE_TASK_TILE_SLOT_KEY}.${taskId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: PROFILE_TASK_TILE_WIDTH,
    zIndex: 2,
});

// The work task tile has no architecture in its payload: the task endpoint is global, so it renders
// outside the per-architecture scope stack and the content language falls back to the app language.
export const workTaskUrlTileDefinition = createUrlTileDefinition({
    codec: createUrlTileCodec<WorkTaskTilePayload>({
        options: [
            { codec: urlTileIntegerCodec, field: 'version', key: 'version' },
            { codec: urlTileStringCodec, field: 'language', key: 'lang' },
            { codec: urlTileKeywordCodec({ closed: false, open: true }), field: 'isCommentsOpen', key: 'comments' },
        ],
        segments: ['taskId'],
        type: JSA_WORK_TASK_TILE_TYPE,
    }),
    // A work task is read on its own, never beside a profile tile, so it owns its comment rail.
    commentsGroup: {
        groupKey: (payload) => payload.taskId,
        isCommentsOpen: (payload) => payload.isCommentsOpen === true,
        role: 'parent',
    },
    maxWidth: WORK_TASK_TILE_WIDTH,
    minWidth: WORK_TASK_TILE_WIDTH,
    render: ({ context, payload }: WorkTaskTileDefinitionProps) => (
        <JobArchitecturePermissionsContextProvider>
            <WorkTaskTile
                payload={payload}
                onClose={() => context.closeTile(WORK_TASK_TILE_SLOT_KEY)}
                onOpenTask={(task) =>
                    context.openTile(createWorkTaskTilePayload({ language: payload.language, taskId: task.id }))
                }
                onUpdatePayload={(nextPayload) => context.updateTile(nextPayload)}
            />
        </JobArchitecturePermissionsContextProvider>
    ),
    schema: workTaskTileSchema,
    slotKey: () => WORK_TASK_TILE_SLOT_KEY,
    tileKey: ({ taskId }) => `${WORK_TASK_TILE_SLOT_KEY}.${taskId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: WORK_TASK_TILE_WIDTH,
    zIndex: 2,
});

const automationTileOptions = [
    { codec: urlTileIntegerCodec, field: 'parentVersion', key: 'parentVersion' },
    { codec: urlTileStringCodec, field: 'taskVersionId', key: 'version' },
    { codec: urlTileStringCodec, field: 'language', key: 'lang' },
    { codec: urlTileStringCodec, field: 'roleId', key: 'parentRole' },
] as const;

// Opened from a job profile an automation tile carries its parent's address as the leading segments; opened
// from a work task it carries only the task's own segments. The parent type segment tells the two apart.
const combineAutomationTileCodecs = <TPayload extends UrlTilePayload & { readonly parentId?: string | undefined }>(
    profileCodec: UrlTileCodec<TPayload>,
    workTaskCodec: UrlTileCodec<TPayload>,
): UrlTileCodec<TPayload> => {
    const typeTokenCount = profileCodec.type.split('.').length;

    return {
        parseTokens: (tokens) => {
            const parentType = tokens[typeTokenCount + 1];

            return parentType === 'role' || parentType === 'level'
                ? profileCodec.parseTokens(tokens)
                : workTaskCodec.parseTokens(tokens);
        },
        serialize: (payload) =>
            payload.parentId === undefined ? workTaskCodec.serialize(payload) : profileCodec.serialize(payload),
        type: profileCodec.type,
    };
};

/** A work task belongs to no job architecture, so only a tile opened from a job profile gets its scope. */
const withOptionalArchitectureScope = (architectureId: string | undefined, tile: ReactNode): ReactNode =>
    architectureId === undefined ? (
        tile
    ) : (
        <JobArchitectureScopeProvider architectureId={architectureId}>{tile}</JobArchitectureScopeProvider>
    );

// Read-only view of a task's stored assessment; it addresses the task like the task tile it was opened from
// so the same query resolves it.
export const automationAssessmentUrlTileDefinition = createUrlTileDefinition({
    codec: combineAutomationTileCodecs(
        createUrlTileCodec<AutomationAssessmentTilePayload>({
            // An optional segment ends segment parsing when absent, so the parent version travels as an option.
            options: automationTileOptions,
            segments: ['architectureId', 'parentType', 'parentId', 'taskId'],
            type: JSA_AUTOMATION_ASSESSMENT_TILE_TYPE,
        }),
        createUrlTileCodec<AutomationAssessmentTilePayload>({
            options: automationTileOptions,
            segments: ['taskId'],
            type: JSA_AUTOMATION_ASSESSMENT_TILE_TYPE,
        }),
    ),
    maxWidth: AUTOMATION_ASSESSMENT_TILE_WIDTH,
    minWidth: AUTOMATION_ASSESSMENT_TILE_WIDTH,
    render: ({ context, payload }: AutomationAssessmentTileDefinitionProps) =>
        withOptionalArchitectureScope(
            payload.architectureId,
            <AutomationAssessmentTile
                payload={payload}
                onClose={() => context.closeTile(AUTOMATION_ASSESSMENT_TILE_SLOT_KEY)}
            />,
        ),
    schema: automationAssessmentTileSchema,
    slotKey: () => AUTOMATION_ASSESSMENT_TILE_SLOT_KEY,
    tileKey: ({ taskId }) => `${AUTOMATION_ASSESSMENT_TILE_SLOT_KEY}.${taskId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: AUTOMATION_ASSESSMENT_TILE_WIDTH,
    zIndex: 3,
});

// How a task's business impact comes about, addressed like the assessment tile. It takes the assessment's slot, so
// one explanation stands beside the task at a time.
export const businessImpactUrlTileDefinition = createUrlTileDefinition({
    codec: combineAutomationTileCodecs(
        createUrlTileCodec<BusinessImpactTilePayload>({
            options: automationTileOptions,
            segments: ['architectureId', 'parentType', 'parentId', 'taskId'],
            type: JSA_BUSINESS_IMPACT_TILE_TYPE,
        }),
        createUrlTileCodec<BusinessImpactTilePayload>({
            options: automationTileOptions,
            segments: ['taskId'],
            type: JSA_BUSINESS_IMPACT_TILE_TYPE,
        }),
    ),
    maxWidth: BUSINESS_IMPACT_TILE_WIDTH,
    minWidth: BUSINESS_IMPACT_TILE_WIDTH,
    render: ({ context, payload }: BusinessImpactTileDefinitionProps) =>
        withOptionalArchitectureScope(
            payload.architectureId,
            <BusinessImpactTile
                payload={payload}
                onClose={() => context.closeTile(AUTOMATION_ASSESSMENT_TILE_SLOT_KEY)}
            />,
        ),
    schema: businessImpactTileSchema,
    slotKey: () => AUTOMATION_ASSESSMENT_TILE_SLOT_KEY,
    tileKey: ({ taskId }) => `${AUTOMATION_ASSESSMENT_TILE_SLOT_KEY}.impact.${taskId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: BUSINESS_IMPACT_TILE_WIDTH,
    zIndex: 3,
});

export const automationStepUrlTileDefinition = createUrlTileDefinition({
    codec: combineAutomationTileCodecs(
        createUrlTileCodec<AutomationStepTilePayload>({
            options: automationTileOptions,
            segments: ['architectureId', 'parentType', 'parentId', 'taskId', 'workflowStepId'],
            type: JSA_AUTOMATION_STEP_TILE_TYPE,
        }),
        createUrlTileCodec<AutomationStepTilePayload>({
            options: automationTileOptions,
            segments: ['taskId', 'workflowStepId'],
            type: JSA_AUTOMATION_STEP_TILE_TYPE,
        }),
    ),
    maxWidth: AUTOMATION_STEP_TILE_WIDTH,
    minWidth: AUTOMATION_STEP_TILE_WIDTH,
    render: ({ context, payload }: AutomationStepTileDefinitionProps) =>
        withOptionalArchitectureScope(
            payload.architectureId,
            <AutomationStepTile payload={payload} onClose={() => context.closeTile(AUTOMATION_STEP_TILE_SLOT_KEY)} />,
        ),
    schema: automationStepTileSchema,
    slotKey: () => AUTOMATION_STEP_TILE_SLOT_KEY,
    tileKey: ({ taskId, workflowStepId }) => `${AUTOMATION_STEP_TILE_SLOT_KEY}.${taskId}.${workflowStepId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: AUTOMATION_STEP_TILE_WIDTH,
    zIndex: 4,
});

export const automationRecommendationUrlTileDefinition = createUrlTileDefinition({
    codec: combineAutomationTileCodecs(
        createUrlTileCodec<AutomationRecommendationTilePayload>({
            options: automationTileOptions,
            segments: ['architectureId', 'parentType', 'parentId', 'taskId', 'automationId'],
            type: JSA_AUTOMATION_RECOMMENDATION_TILE_TYPE,
        }),
        createUrlTileCodec<AutomationRecommendationTilePayload>({
            options: automationTileOptions,
            segments: ['taskId', 'automationId'],
            type: JSA_AUTOMATION_RECOMMENDATION_TILE_TYPE,
        }),
    ),
    maxWidth: AUTOMATION_RECOMMENDATION_TILE_WIDTH,
    minWidth: AUTOMATION_RECOMMENDATION_TILE_WIDTH,
    render: ({ context, payload }: AutomationRecommendationTileDefinitionProps) =>
        withOptionalArchitectureScope(
            payload.architectureId,
            <AutomationRecommendationTile
                payload={payload}
                onClose={() => context.closeTile(AUTOMATION_RECOMMENDATION_TILE_SLOT_KEY)}
            />,
        ),
    schema: automationRecommendationTileSchema,
    slotKey: () => AUTOMATION_RECOMMENDATION_TILE_SLOT_KEY,
    tileKey: ({ automationId, taskId }) => `${AUTOMATION_RECOMMENDATION_TILE_SLOT_KEY}.${taskId}.${automationId}`,
    toProps: (payload, context) => ({ context, payload }),
    width: AUTOMATION_RECOMMENDATION_TILE_WIDTH,
    zIndex: 4,
});

export const jobArchitectureUrlTileDefinitions: readonly UrlTileDefinition[] = [
    jsaProfileDetailUrlTileDefinition,
    jsaFamilyDetailUrlTileDefinition,
    jsaClusterDetailUrlTileDefinition,
    jsaCompareUrlTileDefinition,
    jsaChangelogUrlTileDefinition,
    jsaArchiveUrlTileDefinition,
    jsaLevelComparisonUrlTileDefinition,
    jsaRoleComparisonUrlTileDefinition,
    profileTaskUrlTileDefinition,
    workTaskUrlTileDefinition,
    automationAssessmentUrlTileDefinition,
    businessImpactUrlTileDefinition,
    automationStepUrlTileDefinition,
    automationRecommendationUrlTileDefinition,
];
